import Box from '@mui/material/Box'

/** Figma 6074:13343 — Library tab bar (Question Bank / Template Library). */
export type LibraryTab = 'question-bank' | 'template-library'

const TAB_ACTIVE_BG = '#008cd0'
const TAB_INACTIVE_COLOR = '#6f6b6b'

type TabOption = Readonly<{ value: LibraryTab; label: string; width: number }>

/** Figma 6074:13346 — active pill 154px; second label sized to content without extra slack. */
const LIBRARY_TABS: TabOption[] = [
  { value: 'question-bank', label: 'Question Bank', width: 154 },
  { value: 'template-library', label: 'Template Library', width: 158 },
]

const TAB_GAP_PX = 12

type Props = Readonly<{
  value: LibraryTab
  onChange: (value: LibraryTab) => void
}>

function LibraryTabButton({
  option,
  selected,
  onSelect,
}: Readonly<{
  option: TabOption
  selected: boolean
  onSelect: () => void
}>) {
  return (
    <Box
      component="button"
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onSelect}
      data-testid={`library-tab-${option.value}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        width: option.width,
        height: 40,
        px: 0,
        mb: '-1px',
        borderRadius: selected ? '8px 8px 0 0' : 0,
        border: selected ? `1.5px solid ${TAB_ACTIVE_BG}` : '1.5px solid transparent',
        borderBottom: selected ? `1px solid ${TAB_ACTIVE_BG}` : '1px solid transparent',
        bgcolor: selected ? TAB_ACTIVE_BG : 'transparent',
        color: selected ? '#fff' : TAB_INACTIVE_COLOR,
        cursor: 'pointer',
        fontFamily: 'Roboto, Inter, sans-serif',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        letterSpacing: '0.28px',
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        ...(!selected && {
          '&:hover': { color: TAB_ACTIVE_BG },
        }),
        '&:focus-visible': {
          outline: '2px solid #0fd4fc',
          outlineOffset: 2,
        },
      }}
    >
      {option.label}
    </Box>
  )
}

export function LibrarySegmentTabs({ value, onChange }: Props) {
  return (
    <Box
      role="tablist"
      aria-label="Library sections"
      data-testid="library-tabs"
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: `${TAB_GAP_PX}px`,
        mt: '20px',
        minHeight: 40,
        borderBottom: `1px solid ${TAB_ACTIVE_BG}`,
      }}
    >
      {LIBRARY_TABS.map((option) => (
        <LibraryTabButton
          key={option.value}
          option={option}
          selected={value === option.value}
          onSelect={() => onChange(option.value)}
        />
      ))}
    </Box>
  )
}
